// src/components/Sidebar/Sidebar.tsx

import React, { useState, useEffect } from "react";
import {
  Sidebar,
  Menu,
  MenuItem,
  sidebarClasses,
} from "react-pro-sidebar";
import {
  Box,
  Tooltip,
  Drawer,
  useMediaQuery,
  IconButton,
  tooltipClasses,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronRight,
  ChevronLeft,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";

import logoFull from "../../assets/marites-logo.png";
import logoIcon from "../../assets/MariTest_logomark.png";

import dashboardIcon from "../../assets/dasboard.png";
import allTestsIcon from "../../assets/All-tests.png";
import deviceRegistryIcon from "../../assets/device-registry.png";
import mapIcon from "../../assets/map.png";

/* ---------- Custom Tooltip Styling ---------- */
const ThemedTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#1e293b",
    color: "#f8fafc",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 8,
    padding: "8px 12px",
    border: "1px solid #334155",
    boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
  },
});

interface AppSidebarProps {
  open: boolean;
  onClose: () => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery("(max-width:1200px)");
  const location = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? saved === "true" : false;
  });

  /* Auto-collapse on tablet/small desktop */
  useEffect(() => {
    if (isTablet && !collapsed) setCollapsed(true);
  }, [isTablet, collapsed]);

  const handleToggle = () => {
    if (isMobile) {
      onClose();
    } else {
      const next = !collapsed;
      setCollapsed(next);
      localStorage.setItem("sidebarCollapsed", String(next));
    }
  };

  /* ---------- Fixed Colors (Dark Theme) ---------- */
  const sidebarBg = "#273142";
  const textColor = "#e2e8f0";
  const hoverBg = "#317999";
  const activeBg = "#317999";

  /* ---------- Reusable Icon Wrapper ---------- */
  const IconImg = ({ src }: { src: string }) => (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{
        width: collapsed ? 20 : 22,
        height: collapsed ? 20 : 22,
        objectFit: "contain",
        transition: "all 0.2s ease",
      }}
    />
  );

  const SidebarContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Logo */}
      <Box
        sx={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={collapsed || isMobile ? logoIcon : logoFull}
          alt="MariTest"
          draggable={false}
          sx={{
            height: collapsed || isMobile ? 22 : 26,
            width: "auto",
            objectFit: "contain",
            imageRendering: "crisp-edges",
            transition: "all 0.25s ease",
            userSelect: "none",
          }}
        />
      </Box>

      {/* Menu Items */}
      <Menu
        menuItemStyles={{
          button: ({ active }) => ({
            margin: "4px 8px",
            borderRadius: 12,
            padding: collapsed ? "12px" : "12px 16px",
            color: textColor,
            fontWeight: active ? 700 : 500,
            backgroundColor: active ? activeBg : "transparent",
            transition: "all 0.25s ease",
            "&:hover": {
              backgroundColor: hoverBg,
              transform: "translateX(4px)",
            },
          }),
        }}
      >
        {/* Dashboard Section */}
        {!collapsed && (
          <Box sx={{ px: 2, pt: 1, pb: 0.5, fontSize: 12, opacity: 0.6, color: "#94a3b8" }}>
            Dashboard
          </Box>
        )}

        <ThemedTooltip title="Dashboard" placement="right">
          <span>
            <MenuItem
              active={location.pathname === "/"}
              icon={<IconImg src={dashboardIcon} />}
              component={<Link to="/" />}
            >
              Dashboard
            </MenuItem>
          </span>
        </ThemedTooltip>

        <ThemedTooltip title="All Tests" placement="right">
          <span>
            <MenuItem
              active={location.pathname === "/all-tests"}
              icon={<IconImg src={allTestsIcon} />}
              component={<Link to="/all-tests" />}
            >
              All Tests
            </MenuItem>
          </span>
        </ThemedTooltip>

        {/* Network Section */}
        {!collapsed && (
          <Box sx={{ px: 2, pt: 2, pb: 0.5, fontSize: 12, opacity: 0.6, color: "#94a3b8" }}>
            Network
          </Box>
        )}

        <ThemedTooltip title="Map" placement="right">
          <span>
            <MenuItem
              active={location.pathname === "/map"}
              icon={<IconImg src={mapIcon} />}
              component={<Link to="/map" />}
            >
              Map
            </MenuItem>
          </span>
        </ThemedTooltip>

        <ThemedTooltip title="Device Registry" placement="right">
          <span>
            <MenuItem
              active={location.pathname === "/device-registry"}
              icon={<IconImg src={deviceRegistryIcon} />}
              component={<Link to="/device-registry" />}
            >
              Device Registry
            </MenuItem>
          </span>
        </ThemedTooltip>

        {/* Account Section */}
        {!collapsed && (
          <Box sx={{ px: 2, pt: 2, pb: 0.5, fontSize: 12, opacity: 0.6, color: "#94a3b8" }}>
            Account
          </Box>
        )}

        <ThemedTooltip title="Settings" placement="right">
          <span>
            <MenuItem
              active={location.pathname === "/settings"}
              icon={<SettingsIcon />}
              component={<Link to="/settings" />}
            >
              Settings
            </MenuItem>
          </span>
        </ThemedTooltip>

        <Box sx={{ flexGrow: 1 }} />

        <ThemedTooltip title="Logout" placement="right">
          <span>
            <MenuItem
              icon={<LogoutIcon sx={{ color: "#FFFFFF" }} />}
              style={{ color: "#FFFFFF", fontWeight: 600 }}
              component={<Link to="/logout" />}
            >
              Logout
            </MenuItem>
          </span>
        </ThemedTooltip>
      </Menu>

      {/* Collapse Toggle Button */}
      {!isMobile && (
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "center" }}>
          <IconButton onClick={handleToggle} size="small" sx={{ color: textColor }}>
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return isMobile ? (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <Box sx={{ width: 280, height: "100%", backgroundColor: sidebarBg }}>
        {SidebarContent}
      </Box>
    </Drawer>
  ) : (
    <Sidebar
      collapsed={collapsed}
      width="280px"
      collapsedWidth="80px"
      backgroundColor={sidebarBg}
      rootStyles={{
        position: "sticky",
        top: 0,
        height: "100vh",
        borderRight: "none",
        boxShadow: "none",
        [`.${sidebarClasses.container}`]: {
          backgroundColor: sidebarBg,
          backgroundImage: "none",
          borderRight: "none",
          boxShadow: "none",
        },
      }}
    >
      {SidebarContent}
    </Sidebar>
  );
};

export default AppSidebar;